import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { backupApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsBackupI,
  BackupI,
  ObjPostI,
  BackupExecutionI,
  BackupStatsI,
  NASConnectionI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

// Función para obtener todos los backups con paginación
const getBackups = async ({
  pagination,
  sorting,
  globalFilter,
  backupType,
  status,
  dateFrom,
  dateTo,
}: PropsBackupI): Promise<ServiceResponseI<DataI<BackupI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));

    if (backupType) params.append("backupType", backupType);
    if (status !== undefined) params.append("status", status.toString());
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);

    const { data } = await backupApi.get<ServiceResponseI<DataI<BackupI>>>("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((err as Error).message);
  }
};

// Función para obtener un backup por ID
const getBackupById = async (
  id: number,
): Promise<ServiceResponseI<BackupI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await backupApi.get<ServiceResponseI<BackupI>>(`/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// Función para obtener estadísticas de backups
const getBackupStats = async (): Promise<ServiceResponseI<BackupStatsI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await backupApi.get<ServiceResponseI<BackupStatsI>>(
      "/stats",
      {
        headers: { Authorization: validateToken },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// Función para ejecutar un backup manual
const executeBackup = async (
  backupData: BackupExecutionI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await backupApi.post<ServiceResponseI<string>>(
      "/execute",
      backupData,
      {
        headers: { Authorization: validateToken },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// Función para probar conexión al NAS
const testNASConnection = async (): Promise<
  ServiceResponseI<NASConnectionI>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await backupApi.get<ServiceResponseI<NASConnectionI>>(
      "/test-nas",
      {
        headers: { Authorization: validateToken },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// Función para eliminar un backup
const deleteBackup = async (id: number): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await backupApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      {
        headers: { Authorization: validateToken },
      },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

// Hook para obtener la lista de backups
export const useBackups = ({
  sorting,
  pagination,
  globalFilter,
  backupType,
  status,
  dateFrom,
  dateTo,
}: PropsBackupI) => {
  const backupsQuery = useQuery(
    [
      "backups",
      {
        pagination,
        sorting,
        globalFilter,
        backupType,
        status,
        dateFrom,
        dateTo,
      },
    ],
    () =>
      getBackups({
        pagination,
        sorting,
        globalFilter,
        backupType,
        status,
        dateFrom,
        dateTo,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      staleTime: 1000 * 30, // 30 segundos
    },
  );

  return {
    backupsQuery,
  };
};

// Hook para obtener un backup por ID
export const useBackupById = (id: number) => {
  return useQuery(["backup", id], () => getBackupById(id), {
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutos
  });
};

// Hook para obtener estadísticas de backups
export const useBackupStats = () => {
  return useQuery(["backup-stats"], () => getBackupStats(), {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2, // 2 minutos
  });
};

// Hook para ejecutar un backup manual
export const useExecuteBackup = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (backupData: BackupExecutionI) => executeBackup(backupData),
    {
      onSuccess: () => {
        // Invalidar cachés relacionadas
        queryClient.invalidateQueries(["backups"]);
        queryClient.invalidateQueries(["backup-stats"]);
      },
    },
  );
};

// Hook para probar conexión al NAS
export const useTestNASConnection = () => {
  return useMutation(() => testNASConnection(), {
    // No invalidamos cachés aquí ya que es solo una prueba
  });
};

// Hook para eliminar un backup
export const useDeleteBackup = () => {
  const queryClient = useQueryClient();

  return useMutation((id: number) => deleteBackup(id), {
    onSuccess: () => {
      // Invalidar cachés relacionadas
      queryClient.invalidateQueries(["backups"]);
      queryClient.invalidateQueries(["backup-stats"]);
    },
  });
};

// Hook personalizado para refrescar backups automáticamente
export const useAutoRefreshBackups = (enabled: boolean = false) => {
  const queryClient = useQueryClient();

  return useQuery(["auto-refresh-backups"], () => Promise.resolve(true), {
    enabled,
    refetchInterval: 1000 * 30, // 30 segundos
    refetchIntervalInBackground: false,
    onSuccess: () => {
      queryClient.invalidateQueries(["backups"]);
      queryClient.invalidateQueries(["backup-stats"]);
    },
  });
};
